import { Button, Col, Form, Modal, notification, Row, Select } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import TextArea from "antd/es/input/TextArea";
import {
  useGetPossibleRekStatusListMutation,
  useGetPossibleRekSubStatusListMutation,
  useSetRekStatusMutation,
} from "store/masterdata";

interface Props {
  visible: boolean;
  onClose(): void;
  onSuccess(): void;
  kundennummer: number;
  currentRekStatus: number;
  currentRekSubStatus: number;
  ausfallReason: string;
  isNachkodierung: boolean;
}

function SetRekSubStatusModal({
  visible,
  onClose,
  onSuccess,
  kundennummer,
  currentRekStatus,
  currentRekSubStatus,
  ausfallReason,
  isNachkodierung,
}: Props): JSX.Element {
  const [rekStatusList, setRekStatusList] = useState<number[] | []>([]);
  const [rekSubStatusList, setRekSubStatusList] = useState<number[] | []>([]);
  const [selectedRekStatus, setSelectedRekStatus] = useState(currentRekStatus);
  const [selectedRekSubStatus, setSelectedRekSubStatus] =
    useState(currentRekSubStatus);
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const [getPossibleRekSubStatusList] =
    useGetPossibleRekSubStatusListMutation();
  const [getPossibleRekStatusList] = useGetPossibleRekStatusListMutation();
  const [setRekStatus] = useSetRekStatusMutation();

  useEffect(() => {
    const fetchRekStatus = async () => {
      try {
        const response = await getPossibleRekStatusList({
          rekStatus: selectedRekStatus,
          kundennummer,
        }).unwrap();
        if (!response.hasErrors) {
          let tempRekStatusList = response.result;
          if (
            currentRekStatus &&
            !tempRekStatusList.includes(currentRekStatus)
          ) {
            tempRekStatusList = [...tempRekStatusList, currentRekStatus];
          }
          setRekStatusList(tempRekStatusList);
        }
      } catch (error) {
        console.error("Error", error);
      }
    };
    fetchRekStatus();
  }, []);

  useEffect(() => {
    const fetchRekSubStatus = async () => {
      try {
        const response = await getPossibleRekSubStatusList({
          rekStatus: selectedRekStatus,
          isNachkodierung,
        }).unwrap();
        if (!response.hasErrors) {
          let tempRekSubStatusList = response.result;
          if (
            currentRekSubStatus &&
            !tempRekSubStatusList.includes(currentRekSubStatus)
          ) {
            tempRekSubStatusList = [...tempRekSubStatusList, currentRekStatus];
          }
          setRekSubStatusList(tempRekSubStatusList);
        }
      } catch (error) {
        console.error("Error", error);
      }
    };
    fetchRekSubStatus();
  }, [currentRekStatus, selectedRekStatus]);

  const rekSubStatusOptions = rekSubStatusList.map((option) => ({
    label: t(`masterdata.enums.RekSubStatusEnum.${option}`),
    value: option,
  }));

  const rekStatusOptions = rekStatusList.map((option) => ({
    label: t(`masterdata.enums.RekStatusEnum.${option}`),
    value: option,
  }));

  const onChangeRekSubStatus = (value: number) => {
    setSelectedRekSubStatus(value);
  };

  const onChangeRekStatus = (value: number) => {
    setSelectedRekStatus(value);
  };

  const isDescriptionFieldVisible =
    [90].includes(selectedRekSubStatus) || (isNachkodierung && ausfallReason);
  const isRekSubStatusFieldVisible = [13, 17, 19, 20, 21, 26, 27, 28].includes(
    selectedRekStatus
  );

  const handleSubmit = async (values: {
    rekSubStatus: number;
    rekStatus: number;
    ausfallReason?: string;
  }) => {
    try {
      const dto = {
        kundennummer,
        ...values,
        isNachkodierung,
      };

      if (selectedRekSubStatus !== 90) dto.ausfallReason = "";

      const payload = await setRekStatus(dto).unwrap();

      if (!payload.hasErrors) {
        notification.success({
          message: t("masterdata.lw_details.setStateChangeSuccess"),
        });
        onClose();
        onSuccess();
      } else {
        notification.error({ message: payload.errorMessage });
      }
    } catch (error) {
      console.error("Error", error);
    }
  };

  return (
    <Modal
      footer={null}
      title={
        !isNachkodierung
          ? t("masterdata.lw_details.state_change")
          : t("masterdata.lw_details.nachkodierung")
      }
      visible={visible}
      onCancel={onClose}
      destroyOnClose={false}
    >
      <Form
        id="set-rek-sub-status-form"
        form={form}
        onFinish={handleSubmit}
        layout="vertical"
      >
        <Form.Item
          label={t("masterdata.lw_details.rekStatus")}
          name="rekStatus"
          rules={[{ required: true }]}
          initialValue={currentRekStatus}
        >
          <Select
            options={rekStatusOptions}
            onChange={onChangeRekStatus}
            disabled={isNachkodierung}
          />
        </Form.Item>
        {isRekSubStatusFieldVisible && (
          <Form.Item
            label={t("masterdata.lw_details.rekSubStatus")}
            name="rekSubStatus"
            rules={[{ required: true }]}
            initialValue={currentRekSubStatus}
          >
            <Select
              options={rekSubStatusOptions}
              onChange={onChangeRekSubStatus}
            />
          </Form.Item>
        )}
        {isDescriptionFieldVisible && (
          <Form.Item
            label={t("masterdata.lw_details.ausfallReason")}
            name="ausfallReason"
            rules={[{ required: true }]}
            initialValue={ausfallReason}
          >
            <TextArea
              rows={4}
              maxLength={500}
              showCount
              disabled={isNachkodierung}
            />
          </Form.Item>
        )}
        <Row gutter={20}>
          <Col span={12}>
            <Button onClick={onClose} block>
              {t("abort")}
            </Button>
          </Col>
          <Col span={12}>
            <Button type="primary" htmlType="submit" block>
              {t("save")}
            </Button>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}

export default SetRekSubStatusModal;
